<template>

<Information/>

<div class="bg-white my-8 mx-6">
    <!-- エラーメッセージ -->
    <div class="px-8 pt-12">
        <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />
        <h1 class="text-2xl font-bold pb-2">記事管理</h1>
    </div>
    <div v-if="isLoading" class="py-4">
        <DataLoading />
    </div>
    <div v-else-if="!hasArticle && articles.length === 0" class="text-center pt-6 pb-12 px-8">
        <img :src="notuploadedvideo_articleImg" alt="アイキャッチ" class="w-48 h-48 m-auto">
        <p class="py-8 text-sm">文字起こしされた記事はありません。<br>「<router-link to="/video-management" class="link-styles">動画管理</router-link>」から動画のアップロードを開始してください。</p>
    </div>
    <div v-else>
        <div class="flex overflow-x-auto space-x-1 mt-2 whitespace-nowrap px-8">
            <button v-for="status in articleStatuses" :key="status.value"
                @click="currentFilter = status.value; fetchArticles()"
                :class="{
                    'bg-emerald-500 text-white font-bold': status.value === currentFilter,
                    'bg-gray-100 border border-transparent hover:border-gray-300': status.value !== currentFilter
                }"
                class="text-sm rounded-full px-4 py-2 transition-colors duration-150">
                {{ status.text }}
                <span v-if="status.value === 'unpublished' && unpublishedCount > 0" class="bg-blue-500 text-white rounded-full px-2 py-1 text-xs">{{ unpublishedCount }}</span>
            </button>
        </div>
        <div class="overflow-x-auto px-8 pt-6 pb-12">
            <table class="w-full">
                <thead class="text-sm border-b-2">
                    <tr class="[&>th]:p-4 [&>th]:whitespace-nowrap text-sub">
                        <th class="min-w-40">文字起こし記事</th>
                        <th class="w-32 min-w-32">公開状態</th>
                        <th class="w-32 min-w-32">公開設定</th>
                        <th class="w-16 min-w-16">削除</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="filteredArticles.length === 0">
                        <td colspan="5" class="text-center py-4">該当する記事がありません。</td>
                    </tr>
                    <tr v-else v-for="article in filteredArticles" :key="article.id" class="[&>td]:py-4 [&>td]:px-3 border-b">
                        <td class="min-w-40">
                            <div class="flex flex-wrap flex-col md:flex-row gap-2 items-start md:items-center">
                                <div class="shrink-0 w-full md:w-40 max-w-40">
                                    <div class="aspect-[16/9] w-full">
                                        <img :src="article.video.thumbnail" alt="サムネイル" class="object-cover w-full h-full rounded">
                                    </div>
                                </div>
                                <div class="flex-1 md:min-w-20 space-y-1">
                                    <div class="line-clamp-2 break-all">
                                        {{ article.article_title || article.video.video_title_youtube }}
                                    </div>
                                    <div class="line-clamp-2 break-all">
                                        <a v-if="article.article_url" :href="article.article_url" target="_blank" rel="noopener noreferrer" class="text-xs block break-all link-styles hover:underline" >{{ article.article_url }}</a>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="text-center whitespace-nowrap text-sm">{{ article.video_status_display }}</td>

                        <td class="text-center whitespace-nowrap">
                            <!--下書き-->
                            <button v-if="article.video_status_display === VIDEO_STATUS_PRE_PUBLICATION" @click="navigateToSettings(article.id)" class="relative btn-secondary">
                                設定する
                                <span class="absolute -top-1 -right-1 block h-2 w-2 bg-emerald-500 rounded-full"></span>
                            </button>
                            <!--予約投稿中-->
                            <button v-else-if="article.video_status_display === VIDEO_STATUS_SCHEDULED" @click="navigateToSettings(article.id)" class="relative btn-secondary">
                                変更する
                            </button>
                            <span v-else>-</span>
                        </td>
                        <td class="text-center whitespace-nowrap">
                            <button @click="toggleMenu(article.id)" class="link-styles--gray">
                                <i class="fa-solid fa-ellipsis"></i>
                            </button>
                            <div v-if="isMenuOpen(article.id)" class="absolute right-0 mt-2 w-48 bg-white shadow-xl z-10">
                                <ul class="py-1">
                                    <li>
                                        <div v-if="canArticleUpdate">
                                          <button @click="navigateToSettings(article.id)" 
                                              class="link-styles--gray block px-4 py-2 text-sm" 
                                              :class="{'opacity-50 cursor-not-allowed'
                                              : article.video_status_display !== VIDEO_STATUS_PRE_PUBLICATION && article.video_status_display !== VIDEO_STATUS_SCHEDULED && article.video_status_display !== VIDEO_STATUS_PUBLISHED}" 
                                              :disabled="article.video_status_display !== VIDEO_STATUS_PRE_PUBLICATION && article.video_status_display !== VIDEO_STATUS_SCHEDULED && article.video_status_display !== VIDEO_STATUS_PUBLISHED">
                                              記事編集
                                          </button>
                                        </div>
                                        <button @click="openDeleteModal(article.id)"
                                            class="text-red-600 block px-4 py-2 text-sm"
                                            :class="{'opacity-50 cursor-not-allowed': article.video_status_display !== VIDEO_STATUS_PUBLISHED}"
                                            :disabled="article.video_status_display !== VIDEO_STATUS_PUBLISHED">
                                            掲載取りやめ
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <BasePaginator :data="articles" :total="total" :page="page" :pageStartNum="pageStartNum" :pageEndNum="pageEndNum" :hasNext="hasNext" :hasPrevious="hasPrevious" :fetchFunction="fetchArticles" @page-changed="fetchArticles" />
        </div>
    </div>
    <!--モーダル-->
    <div v-if="isModalOpen" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full p-4" @click.self="closeModal()">
        <div class="relative top-20 mx-auto max-w-sm shadow rounded-lg bg-white text-center pt-12 pb-8 px-6 space-y-4">
            <div class="py-4 space-y-4">
                <h3 class="text-xl font-bold">以下の記事を削除します。よろしいですか？</h3>
                <p class="text-sm p-4 bg-gray-100 text-left"><span class="text-sub">記事タイトル</span><br>{{ selectedArticleTitle }}</p>
                <p class="text-red-600 text-sm text-left">記事が削除されるまで最大で15分かかる場合があります。「削除する」を押すと取り消しはできません。</p>
            </div>
            <div class="flex justify-center gap-2">
                <button @click="closeModal()" class="btn-secondary">キャンセル</button>
                <button @click="deleteArticle()" class="btn bg-red-600 text-white hover:bg-red-700">削除する</button>
            </div>
        </div>
    </div>

</div>
</template>

<script>
import axios from 'axios';
import notuploadedvideo_articleImg from '@/assets/notuploadedvideo_article.png';
import {
    VIDEO_STATUS_PRE_PUBLICATION,
    VIDEO_STATUS_SCHEDULED,
    VIDEO_STATUS_PUBLISHED
} from '@/plugins/constants';
import Information from './InformationTable.vue';
import DataLoading from './DataLoading.vue';
import BasePaginator from './BasePaginator.vue';
import ErrorMessage from './ErrorMessage.vue';

export default {
    name: 'ArticleManagement',
    components: {
        Information,
        DataLoading,
        BasePaginator,
        ErrorMessage,
    },
    data() {
        return {
            articles: [],
            filteredArticles: [],
            articleStatuses: [{
                    text: 'すべて',
                    value: 'all'
                },
                {
                    text: VIDEO_STATUS_PRE_PUBLICATION,
                    value: 'pre_publication'
                },
                {
                    text: VIDEO_STATUS_SCHEDULED,
                    value: 'scheduled'
                },
                {
                    text: VIDEO_STATUS_PUBLISHED,
                    value: 'published'
                }
            ],
            VIDEO_STATUS_PRE_PUBLICATION,
            VIDEO_STATUS_SCHEDULED,
            VIDEO_STATUS_PUBLISHED,
            unpublishedCount: 0,
            notuploadedvideo_articleImg,
            currentFilter: 'all', // 現在選択中のフィルタ
            currentPage: 1, // 現在のページ
            articlesPerPage: 10, // ページごとの記事数
            openMenuId: null,
            isModalOpen: false,
            selectedArticleTitle: '', // 選択された記事のタイトルを保持するプロパティ
            errorMessage: '', // エラーメッセージを保持する変数
            canArticleUpdate: true,
            isLoading: true,
            page: 1,
            pageStartNum: 1,
            pageEndNum: 1,
            total: 0,
            hasNext: false,
            hasPrevious: false,
            hasArticle: false,
        };
    },
    created() {
        this.fetchArticles();
    },
    methods: {
        fetchArticles(pageNum=1) {
            this.page = pageNum;
            if(this.page < 1) {
                this.page = 1;
            }
            const accessToken = this.$store.state.accessToken;
            const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
            axios.get(`${apiEndpoint}/articles/?page=${this.page}&status=${this.currentFilter}`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                })
                .then(response => {
                    this.articles = response.data.results;
                    this.filteredArticles = this.articles;
                    this.unpublishedCount = this.articles.filter(article => article.status_upload === 'unpublished').length;
                    this.isLoading = false;
                    this.hasNext = response.data.hasNext;
                    this.hasPrevious = response.data.hasPrevious;
                    this.total = response.data.total;
                    this.pageStartNum = response.data.start;
                    this.pageEndNum = response.data.end;
                    if(this.articles && this.articles.length > 0) {
                        this.hasArticle = true;
                    }
                })
                .catch(error => {
                    console.log(error);
                    this.errorMessage = '記事の取得に失敗しました。画面の再読み込みを行うと解決する場合があります。'; // エラーメッセージをセット
                });
            // 記事編集ON/OFF判定
            const articleUpdateFlg = process.env.VUE_APP_NEWS_UPDATE_ONOFF;
            if (articleUpdateFlg == 'OFF') {
                this.canArticleUpdate = false;
            }
        },

        clearErrorMessage() {
            this.errorMessage = '';
        },

        navigateToSettings(articleId) {
            this.$router.push({
                name: 'ArticleSettings',
                params: {
                    id: articleId
                }
            });
        },

        requestDelete(articleVideo) {
            const accessToken = this.$store.state.accessToken;
            const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
            let postData = {
                video_id: articleVideo.video_id,
            };
            console.log(articleVideo);
            axios.post(`${apiEndpoint}/request-delete/`, postData, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                })
                .then(response => {
                    console.log(response);
                    this.$router.push({
                        name: 'ArticleManagement'
                    }).then(() => {
                        this.fetchArticles();
                    });
                })
                .catch(error => {
                    console.error(error);
                });
        },
        toggleMenu(articleId) {
            this.openMenuId = this.openMenuId === articleId ? null : articleId;
        },
        isMenuOpen(articleId) {
            return this.openMenuId === articleId;
        },
        openDeleteModal(articleId) {
            const article = this.articles.find(article => article.id === articleId);
            if (article) {
                this.selectedArticleTitle = article.article_title; // 記事のタイトルをセット
                this.selectedArticle = article; // 記事データをセット
            }
            this.isModalOpen = true;
        },
        closeModal() {
            this.isModalOpen = false;
            this.$router.push({
                name: 'ArticleManagement'
            }).then(() => {
                this.fetchArticles();
            });
        },
        deleteArticle() {
            if (this.selectedArticle && this.selectedArticle.video) {
                this.requestDelete(this.selectedArticle.video);
                this.toggleMenu(this.selectedArticle.id); 
                this.closeModal();

            }
        },

    }
};
</script>
